import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import { useTheme } from '../context/ThemeContext';
import Swal from 'sweetalert2';
import axios from 'axios';


const ScheduledSMS = () => {
  const { isDark } = useTheme();
  const [scheduledSms, setScheduledSms] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: '',
  });
  const [triggerCategory, setTriggerCategory] = useState('first_service_first_timer');
  const [triggerService, setTriggerService] = useState('first_service');
  const [message, setMessage] = useState('');


  const Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true
  });

  // API Base URL - prefer device host for mobile access
  const deriveApiBase = () => {
    // Force production URL when deployed
    if (window.location.hostname === 'rccgtr.net' || window.location.hostname.includes('rccgtr')) {
      return 'https://rccgtr.net/api';
    }
    
    // Development only
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    }
    
    // Fallback to relative path
    return '/api';
  };
  const API_BASE_URL = deriveApiBase();

  useEffect(() => {
    loadScheduledSms();
    loadStats();
    const interval = setInterval(() => {
      loadScheduledSms();
      loadStats();
    }, 30000);
    return () => clearInterval(interval);
  }, [pagination.page, filter, categoryFilter, searchTerm, dateRange]);

  const loadScheduledSms = async () => {
    try {
      setLoading(true);
      const query = {
        page: pagination.page,
        limit: pagination.limit,
        ...(filter !== 'all' ? { status: filter } : {}),
        ...(categoryFilter ? { category: categoryFilter } : {}),
        ...(searchTerm ? { search: searchTerm } : {}),
        ...(dateRange.startDate ? { startDate: dateRange.startDate } : {}),
        ...(dateRange.endDate ? { endDate: `${dateRange.endDate} 23:59:59` } : {}),
      };
      const { data } = await axios.get(`${API_BASE_URL}/sms/scheduled`, { params: query });
      if (data.success) {
        setScheduledSms(data.data);
        setPagination(prev => ({
          ...prev,
          total: data.pagination.total,
          pages: data.pagination.pages,
        }));
      } else {
        console.error('Failed to load scheduled SMS:', data.error);
      }
    } catch (error) {
      console.error('Error loading scheduled SMS:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/sms/stats`);
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };
 

  const manualTrigger = async () => {
    try {
      setMessage('⏳ Triggering... Please wait...');
      setLoading(true);

      const { data } = await axios.post(`${API_BASE_URL}/sms/trigger/${triggerCategory}`);
      if (data.success) {
       const { scheduled, processed, failed } = data;
      setMessage(
        `✅ Trigger completed for ${triggerCategory}: ` +
        `${scheduled || 0} scheduled, ${processed || 0} sent, ${failed || 0} failed`
      );
      
      Toast.fire({
        icon: 'success',
        title: `${processed || 0} SMS sent successfully!`
      });
      
      // Reload data after a short delay
      setTimeout(() => {
        loadStats();
        loadScheduledSms();
      }, 2000);

        loadStats();
        loadScheduledSms();
      } else {
       setMessage(`❌ Error: ${data.error || 'Failed to trigger'}`);
      Toast.fire({
        icon: 'error',
        title: 'Failed to trigger SMS'
      });
      }
    } catch (e) {
      console.error('Error triggering scheduler:', e);
    setMessage('❌ Error triggering scheduler');
    Toast.fire({
      icon: 'error',
      title: 'Error triggering scheduler'
    });
    } finally {
      setLoading(false);
    }
  };

  const removeScheduledSms = async id => {
    // Use SweetAlert instead of window.confirm
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });
  
    if (result.isConfirmed) {
      try {
        const { data } = await axios.delete(`${API_BASE_URL}/sms/scheduled/${id}`);
        
        if (data.success) {
          // Show success message
          Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: 'Scheduled SMS has been deleted.',
            timer: 2000,
            showConfirmButton: false
          });
          
          loadScheduledSms();
          loadStats();
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Oops...!',
            text: `Failed to remove SMS: ${data.error}`,
            timer: 2000,
            showConfirmButton: false
          });
        }
      } catch (error) {
        console.error('Error removing SMS:', error);
        Swal.fire({
          icon: 'error',
          title: 'Oops...!',
          text: 'Error removing SMS',
          timer: 2000,
          showConfirmButton: false
        });
      }
    }
  };

  const getStatusColor = status => {
    switch (status) {
      case 'scheduled':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
      case 'sending':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
      case 'sent':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getStatusIcon = status => {
    switch (status) {
      case 'scheduled':
        return '⏰';
      case 'sending':
        return '📤';
      case 'sent':
        return '✅';
      case 'failed':
        return '❌';
      default:
        return '❓';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
  
    try {
      // If backend sends MySQL DATETIME like "2025-09-14 12:00:00"
      if (typeof dateString === 'string' && dateString.includes(' ')) {
        const [datePart, timePart] = dateString.split(' ');
        const [year, month, day] = datePart.split('-');
        const [hour, minute, second] = timePart.split(':');
  
        const d = new Date(year, month - 1, day, hour, minute, second);
        return d.toLocaleString('en-US', {
          month: 'numeric',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          second: '2-digit',
          hour12: true,
        });
      }
  
      // If backend already sends ISO string or Date
      const d = new Date(dateString);
      return isNaN(d) ? '' : d.toLocaleString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      });
    } catch (err) {
      console.error('formatDate error:', err, dateString);
      return '';
    }
  };

  const getRecipientName = sms => {
    if (sms.recipient_name || sms.recipient_last_name) {
      return `${sms.recipient_name || ''} ${sms.recipient_last_name || ''}`.trim();
    }
    return sms.phone;
  };

  const handleFilterChange = value => {
    setFilter(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  return (
    <Layout title="Scheduled SMS">
      <div className="max-w-7xl mx-auto">
        {/* Header with Stats */}
        <div
          className={`rounded-xl shadow-lg p-6 mb-6 ${
            isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'
          }`}
        >
          <h2 className="text-xl font-semibold mb-4">SMS Scheduler Status</h2>

          {message && (
            <div
              className={`mb-4 p-3 rounded ${
                message.includes('Triggered')
                  ? 'bg-green-100 text-green-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}
            >
              {message}
              <button className="float-right font-bold" onClick={() => setMessage('')}>
                ×
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
            <div className="text-center">
              <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                {stats.total || 0}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Total</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                {stats.statusBreakdown?.find(s => s.status === 'scheduled')?.count || 0}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Pending</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {stats.statusBreakdown?.find(s => s.status === 'sending')?.count || 0}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Sending</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {stats.statusBreakdown?.find(s => s.status === 'sent')?.count || 0}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Sent</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                {stats.statusBreakdown?.find(s => s.status === 'failed')?.count || 0}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Failed</div>
            </div>
          </div>

          {/* Manual Trigger */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-sm mb-1">Category</label>
              <select
                value={triggerCategory}
                onChange={e => setTriggerCategory(e.target.value)}
                className="border rounded px-3 py-2 w-full"
              >
                <option value="first_service_first_timer">First Service - First Timer</option>
                <option value="first_service_returning_member">First Service - Returning</option>
                <option value="second_service_first_timer">Second Service - First Timer</option>
                <option value="second_service_returning_member">Second Service - Returning</option>
                <option value="first_service_new_birth">First Service - New Birth</option>
                <option value="second_service_new_birth">Second Service - New Birth</option>
                <option value="sunday_reminder">Sunday Reminder</option>
                <option value="midweek_reminder">Midweek Reminder</option>
                <option value="birthday">Birthday</option>
              </select>
            </div>
            {/* <div>
              <label className="block text-sm mb-1">Service</label>
              <select
                value={triggerService}
                onChange={e => setTriggerService(e.target.value)}
                className="border rounded px-3 py-2 w-full"
              >
                <option value="first_service">First Service</option>
                <option value="second_service">Second Service</option>
                <option value="midweek">Midweek</option>
              </select>
            </div> */}
            <div className="md:col-span-2 flex items-end">
              <button
                onClick={manualTrigger}
                className="bg-indigo-600 text-white px-4 py-2 rounded w-full md:w-auto"
              >
                Manual Trigger
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div
          className={`rounded-xl shadow p-4 mb-6 ${
            isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'
          }`}
        >
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-sm mb-1">Status</label>
              <select
                value={filter}
                onChange={e => handleFilterChange(e.target.value)}
                className="border px-3 py-2 rounded"
              >
                <option value="all">All</option>
                <option value="scheduled">Scheduled</option>
                <option value="sending">Sending</option>
                <option value="sent">Sent</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm mb-1">Category</label>
              <select
                value={categoryFilter}
                onChange={e => { setCategoryFilter(e.target.value); setPagination(prev => ({ ...prev, page: 1 })); }}
                className="border px-3 py-2 rounded"
              >
                <option value="">All</option>
                <option value="first_service_first_timer">First Service - First Timer</option>
                <option value="first_service_returning_member">First Service - Returning</option>
                <option value="second_service_first_timer">Second Service - First Timer</option>
                <option value="second_service_returning_member">Second Service - Returning</option>
                <option value="expression_church_first_timer">Expression Church - First Timer</option>
                <option value="expression_church_returning_member">Expression Church - Returning</option>
                <option value="first_service_new_birth">First Service - New Birth</option>
                <option value="second_service_new_birth">Second Service - New Birth</option>
                <option value="sunday_reminder">Sunday Reminder</option>
                <option value="midweek_reminder">Midweek Reminder</option>
                <option value="birthday">Birthday</option>
              </select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm mb-1">Search</label>
              <input
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Phone, message, category..."
                className="border px-3 py-2 rounded w-full"
              />
            </div>
            <div>
              <label className="block text-sm mb-1">Start Date</label>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={e => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                className="border px-3 py-2 rounded"
              />
            </div>
            <div>
              <label className="block text-sm mb-1">End Date</label>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={e => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                className="border px-3 py-2 rounded"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div
          className={`rounded-xl shadow-lg ${
            isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'
          }`}
        >
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className={`${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
                <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                    SN
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                    Recipient
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                    Message
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                    Scheduled For
                  </th>
                 
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {scheduledSms.map((sms, index) => (
                  <tr
                    key={sms.id}
                    className={`${
                      isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                    } transition-colors`}
                  >
                    <td className="px-4 py-3">{index + 1}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                          sms.status
                        )}`}
                      >
                        <span className="mr-1">{getStatusIcon(sms.status)}</span>
                        {sms.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div>
                        <div className="font-medium">{getRecipientName(sms)}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                          {sms.recipient_type}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium">{sms.phone}</td>
                    <td className="px-4 py-3 text-sm max-w-xs">
                      <div className="truncate" title={sms.message}>
                        {sms.message}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="capitalize">{sms.category}</span>
                    </td>
                    <td className="px-4 py-3 text-sm">{formatDate(sms.scheduled_for)}</td>
                    <td className="px-4 py-3 text-right text-sm">
                      <button
                        onClick={() => removeScheduledSms(sms.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex justify-between items-center p-3 text-sm">
              <button
                onClick={() =>
                  setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))
                }
                disabled={pagination.page === 1}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Previous
              </button>
              <span>
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                onClick={() =>
                  setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))
                }
                disabled={pagination.page === pagination.pages}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ScheduledSMS;
